import React, { memo, useEffect, useMemo, useState } from 'react'
import Header from './Header';
import { discardSentenceForReviewer, discardSentenceForUser, getKnownLangaugesForStudent, getLots, getRejectedSentencesForReviewer, pullMoreLots, releaseLotForUser, resolveSentenceForReviwer, resolveSentenceForUser, revertLotForUser, submitLotForUser } from '@/store/thunks/studentTTT';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { setEditSentences, setSelectedLotId, setSelectedTargetId, setSelectedTargetName, setShowMarkedSentencesTTT } from '@/store/slices/studentDashboard';
import GenericTableStudent from '../GenericTableStudent';
import apiClient from '@/services/api/apiClientStudent';
import { BASE_URL } from '@/utils/constants';

const CommentsModal = memo(function CommentsModal({
    isOpen,
    comments = [],
    onClose,
    loading = false
}) {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-screen overflow-y-auto">
                <div className="p-6">
                    <div className="flex justify-between items-center mb-6 border-b pb-4">
                        <h3 className="text-2xl font-bold text-gray-800">Review Comments</h3>
                        <button
                            onClick={onClose}
                            className="text-gray-500 hover:text-gray-700 text-3xl font-light hover:scale-110 transition"
                        >
                            ×
                        </button>
                    </div>

                    {loading ? (
                        <div className="text-center py-12">
                            <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-amber-500 border-t-transparent"></div>
                            <p className="mt-4 text-gray-600">Loading comments...</p>
                        </div>
                    ) : comments.length === 0 ? (
                        <div className="text-center py-16 bg-gray-50 rounded-lg">
                            <p className="text-gray-500 text-lg">No review comments yet.</p>
                        </div>
                    ) : (
                        <div className="space-y-5">
                            {comments.map((c, i) => (
                                <div
                                    key={i}
                                    className="bg-gradient-to-r from-gray-50 to-gray-100 p-5 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow"
                                >
                                    <div className="flex items-start justify-between mb-2">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 bg-amber-500 text-white rounded-full flex items-center justify-center font-bold text-sm">
                                                {c.studentUsername?.[0]?.toUpperCase() || "S"}
                                            </div>
                                            <div>
                                                <p className="font-semibold text-gray-800">
                                                    {c.studentUsername || "Unknown Student"}
                                                </p>
                                                <p className="text-sm text-amber-600 font-medium">
                                                    Target: {c.targetName || "Unknown"}
                                                </p>
                                            </div>
                                        </div>
                                        <span className="text-xs text-gray-500 bg-gray-200 px-3 py-1 rounded-full">
                                            {new Date(c.createdAt).toLocaleDateString("en-IN", {
                                                day: "numeric",
                                                month: "short",
                                                year: "numeric",
                                                hour: "2-digit",
                                                minute: "2-digit"
                                            })}
                                        </span>
                                    </div>

                                    <div className="mt-3 pl-13">
                                        <p className="text-gray-800 whitespace-pre-wrap leading-relaxed bg-white p-4 rounded-lg border-l-4 border-amber-400">
                                            {c.comment}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
});

const LotsForTTT = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [knownLanguages, setKnownLanguages] = useState(null);
    const [selectedLanguage, setSelectedLanguage] = useState({
        languageId: "",
        languageName: "",
    });
    const { role, userId } = useSelector((state) => state.studentAuth);
    const [targetName, setTargetName] = useState("");
    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedFile, setSelectedFile] = useState(null);
    const [tableData, setTableData] = useState([]);

    const [pageIndex, setPageIndex] = useState(0);
    const [dataVersion, setDataVersion] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [activeTab, setActiveTab] = useState("active"); // active | rejected
    const [subTab, setSubTab] = useState("assigned");
    const [pendingAction, setPendingAction] = useState(null);
    const [editingRowIndex, setEditingRowIndex] = useState(null); // Track which row is being edited
    const [editedTexts, setEditedTexts] = useState({}); // Store edited sourceText temporarily

    const [selectedRow, setSelectedRow] = useState(null);


    const [commentsModal, setCommentsModal] = useState({
        isOpen: false,
        sentenceId: null,
        comments: [],
        loading: false
    });


    const paginatedData = useMemo(() => {
        const start = pageIndex * pageSize;
        const end = start + pageSize;
        return files.slice(start, end);
    }, [files, pageIndex, pageSize]);

    const hasNextPage = (pageIndex + 1) * pageSize < files.length;


    // Fetch data from API
    useEffect(() => {
        if (activeTab === "active") {
            getLotsForUser();
            getKnownLanguages();
        } else if (activeTab === "rejected") {
            getRejectedSentencesForUser();
        }
    }, [activeTab, subTab]);

    const getLotsForUser = async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await dispatch(getLots(subTab));

            if (res.data && Array.isArray(res.data) && res.data.length > 0) {
                setFiles(res.data);
            } else {
                setFiles([]);
                setSelectedFile([]);
                // Each lot expected to have some text data for now
                setTableData([]);
            }
        } catch (err) {
            console.error("❌ Error fetching lots:", err);
            setError(err.message || "Failed to fetch lots.");
            toast.error("Failed to load lots. Please try again later.");
        } finally {
            setLoading(false);
        }
    };

    const getRejectedSentencesForUser = async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await dispatch(getRejectedSentencesForReviewer('ttt'));
            if (res.data && Array.isArray(res.data)) {
                const freshData = res.data;
                setFiles(freshData);
                setTableData(freshData.map(item => ({
                    ...item,
                    targetText: item.translatedText || ""
                })));
                setDataVersion(v => v + 1); // ← Force update
            } else {
                setFiles([]);
                setTableData([]);
            }
        } catch (err) {
            console.log(err)
            toast.error(err.message);
        } finally {
            setLoading(false);
        }
    };

    const getKnownLanguages = async () => {
        try {
            setLoading(true);
            setError(null);
            const res = await dispatch(getKnownLangaugesForStudent());
            if (res && Array.isArray(res.data) && res.data.length > 0) {
                setKnownLanguages(res.data.filter((lang) => lang.languageId !== 1));
            } else {
                setKnownLanguages([]);
            }
        }
        catch (error) {
            console.log("Error fetching known languages:", error);
        }
    }

    const handleLanguageChange = (e) => {
        const selectedId = e.target.value;
        const selectedName =
            e.target.options[e.target.selectedIndex].getAttribute("data-name");

        setSelectedLanguage({
            languageId: selectedId,
            languageName: selectedName,
        });

    };

    const releaseLot = async () => {
        setPendingAction("releaseLot");

        // 🟠 Styled confirmation toast (matching your app’s theme)
        toast.info(
            ({ closeToast }) => (
                <div className="flex flex-col items-center text-center px-4 py-2">
                    <p className="mb-4 text-gray-800 text-sm sm:text-base font-medium">
                        Do you want to release Extra Lot?
                    </p>
                    <div className="flex gap-3 justify-center">
                        <button
                            onClick={async () => {
                                closeToast();
                                await handleRelease();
                            }}
                            className="px-4 py-1.5 bg-[#ec7026] hover:bg-[#d15c14] text-white rounded-md font-medium text-sm shadow-sm transition-all duration-200"
                        >
                            ✅ Yes, release
                        </button>
                        <button
                            onClick={() => {
                                toast.info("❌ Pull operation cancelled.", {
                                    position: "top-center",
                                    autoClose: 2000,
                                });
                                closeToast();
                                setPendingAction(null);
                            }}
                            className="px-4 py-1.5 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-md font-medium text-sm shadow-sm transition-all duration-200"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            ),
            {
                toastId: "release-lot-confirmation",
                autoClose: false,
                closeOnClick: false,
                draggable: false,
                position: "top-center",
                hideProgressBar: true,
            }
        );

        // 🧩 Helper function for actual pull logic
        const handleRelease = async () => {
            setLoading(true);
            setError(null);
            try {
                const res = await dispatch(releaseLotForUser(selectedRow.original.lotId, selectedRow.original.targetId));
                if (res.success) {
                    toast.success("🎉 Lot released!", {
                        position: "top-center",
                        autoClose: 2500,
                    });
                    await getLotsForUser();
                } else {
                    toast.error(res.message, {
                        position: "top-center",
                        autoClose: 2500,
                    });
                    await getLotsForUser();
                }
            } catch (err) {
                console.error("❌ Error pulling lots:", err);
                setError(err.message || "Failed to release lots.");
                toast.error("⚠️ Failed to release lot. Please try again later.", {
                    position: "top-center",
                });
            } finally {
                setPendingAction(null);
                setLoading(false);
            }
        };
    };


    const pullLots = async (selectedLanguage) => {
        if (!selectedLanguage?.languageId) {
            toast.warn("⚠️ Please select a language before pulling more lots.", {
                position: "top-center",
            });
            return;
        }

        setPendingAction("pullLots");

        // 🟠 Styled confirmation toast (matching your app’s theme)
        toast.info(
            ({ closeToast }) => (
                <div className="flex flex-col items-center text-center px-4 py-2">
                    <p className="mb-4 text-gray-800 text-sm sm:text-base font-medium">
                        Do you want to pull more lots for{" "}
                        <span className="font-semibold text-[#ec7026]">
                            {selectedLanguage.languageName}
                        </span>
                        ?
                    </p>
                    <div className="flex gap-3 justify-center">
                        <button
                            onClick={async () => {
                                closeToast();
                                await handlePull(selectedLanguage.languageId);
                            }}
                            className="px-4 py-1.5 bg-[#ec7026] hover:bg-[#d15c14] text-white rounded-md font-medium text-sm shadow-sm transition-all duration-200"
                        >
                            ✅ Yes, Pull
                        </button>
                        <button
                            onClick={() => {
                                toast.info("❌ Pull operation cancelled.", {
                                    position: "top-center",
                                    autoClose: 2000,
                                });
                                closeToast();
                                setPendingAction(null);
                            }}
                            className="px-4 py-1.5 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-md font-medium text-sm shadow-sm transition-all duration-200"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            ),
            {
                toastId: "pull-lot-confirmation",
                autoClose: false,
                closeOnClick: false,
                draggable: false,
                position: "top-center",
                hideProgressBar: true,
            }
        );

        // 🧩 Helper function for actual pull logic
        const handlePull = async (lang) => {
            setLoading(true);
            setError(null);
            try {
                const res = await dispatch(pullMoreLots(lang));

                if (res.success) {
                    toast.success("🎉 New lot pulled successfully!", {
                        position: "top-center",
                        autoClose: 2500,
                    });
                    await getLotsForUser();
                } else {
                    toast.error(res.message)
                }
            } catch (err) {
                console.error("❌ Error pulling lots:", err);
                setError(err.message || "Failed to pull lots.");
                toast.error("⚠️ Failed to pull more lots. Please try again later.", {
                    position: "top-center",
                });
            } finally {
                setPendingAction(null);
                setLoading(false);
            }
        };
    };

    const handleTargetChange = React.useCallback((index, value) => {
        setTableData(prev => {
            const updated = [...prev];
            updated[index] = { ...updated[index], targetText: value };
            return updated;
        });
    }, []);


    const goToLot = async (row) => {
        await dispatch(setSelectedLotId(row.original.lotId));
        await dispatch(setSelectedTargetId(row.original.targetId));
        await dispatch(setSelectedTargetName(row.original.targetLangName));
        const showMarkedSentence = (row.original.studentStatus === 'COMPLETED' && row.original.reviewerStatus === 'COMPLETED') || (row.original.studentStatus === 'RESUBMITTED' && row.original.reviewerStatus === 'UNDER_REVIEW') || (row.original.studentStatus === 'IN_PROGRESS' && row.original.reviewerStatus === 'CHANGES_REQUIRED') || (row.original.studentStatus === 'SUBMITTED' && row.original.reviewerStatus === 'UNDER_REVIEW');
        await dispatch(setShowMarkedSentencesTTT(showMarkedSentence))
        const editSentences = row.original.studentStatus === 'IN_PROGRESS' || row.original.studentStatus === 'ASSIGNED';
        await dispatch(setEditSentences(editSentences));
        navigate(`/${role === 'REVIEWER' ? 'reviewer' : 'student'}/lots-for-TTT/TTT/${row.original.lotId}`);
    }

    const submitLot = async (row) => {
        const lotId = row.original.lotId;
        const targetName = row.original.targetLangName || "this lot";

        // Confirmation Toast
        toast.info(
            ({ closeToast }) => (
                <div className="flex flex-col gap-4 p-2">
                    <p className="text-gray-800 font-medium">
                        Are you sure you want to <span className="text-amber-600 font-bold">submit</span> this lot?
                    </p>
                    <p className="text-sm text-gray-600">
                        Lot ID: <span className="font-mono font-bold">{lotId}</span>
                        <br />
                        Target: <span className="font-medium">{targetName}</span>
                    </p>
                    <p className="text-sm text-red-600 font-medium">
                        This action cannot be undone!
                    </p>

                    <div className="flex gap-3 justify-center mt-4">
                        {/* Confirm Button */}
                        <button
                            onClick={async () => {
                                closeToast();
                                await handleSubmit(); // Proceed with submission
                            }}
                            className="px-5 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg font-medium shadow-md transition-all"
                        >
                            Yes, Submit
                        </button>

                        {/* Cancel Button */}
                        <button
                            onClick={() => {
                                closeToast();
                                toast.info("Submission cancelled.", {
                                    autoClose: 2000,
                                    position: "top-center",
                                });
                            }}
                            className="px-5 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-medium transition-all"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            ),
            {
                toastId: "submit-lot-confirmation",
                position: "top-center",
                autoClose: false,
                closeOnClick: false,
                draggable: false,
                closeButton: false,
                className: "border-2 border-amber-400 rounded-xl shadow-2xl",
            }
        );

        // Actual submission logic (only runs after user clicks "Yes")
        const handleSubmit = async () => {
            setLoading(true);
            setError(null);

            try {
                const res = await dispatch(submitLotForUser(lotId, row.original.targetId));

                if (res.success) {
                    toast.success("Lot submitted successfully!", {
                        position: "top-right",
                        autoClose: 3000,
                    });
                    // Optional: refresh data or redirect
                    await getLotsForUser();
                } else {
                    toast.error(res.message, {
                        position: "top-right",
                    });
                }
            } catch (err) {
                console.log(err)
                toast.error(err.message, {
                    position: "top-center",
                });
            } finally {
                setLoading(false);
            }
        };
    };

    const revertLot = async (row) => {
        const lotId = row.original.lotId;
        const targetName = row.original.targetLangName || "this lot";

        // Confirmation Toast
        toast.info(
            ({ closeToast }) => (
                <div className="flex flex-col gap-4 p-2">
                    <p className="text-gray-800 font-medium">
                        Are you sure you want to <span className="text-amber-600 font-bold">revert</span> this lot?
                    </p>
                    <p className="text-sm text-gray-600">
                        Lot ID: <span className="font-mono font-bold">{lotId}</span>
                        <br />
                        Target: <span className="font-medium">{targetName}</span>
                    </p>
                    <p className="text-sm text-red-600 font-medium">
                        This action cannot be undone!
                    </p>

                    <div className="flex gap-3 justify-center mt-4">
                        {/* Confirm Button */}
                        <button
                            onClick={async () => {
                                closeToast();
                                await handleSubmit(); // Proceed with submission
                            }}
                            className="px-5 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg font-medium shadow-md transition-all"
                        >
                            Yes, Submit
                        </button>

                        {/* Cancel Button */}
                        <button
                            onClick={() => {
                                closeToast();
                                toast.info("Submission cancelled.", {
                                    autoClose: 2000,
                                    position: "top-center",
                                });
                            }}
                            className="px-5 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-medium transition-all"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            ),
            {
                toastId: "revert-lot-confirmation",
                position: "top-center",
                autoClose: false,
                closeOnClick: false,
                draggable: false,
                closeButton: false,
                className: "border-2 border-amber-400 rounded-xl shadow-2xl",
            }
        );

        // Actual submission logic (only runs after user clicks "Yes")
        const handleSubmit = async () => {
            setLoading(true);
            setError(null);

            try {
                const res = await dispatch(revertLotForUser(lotId, row.original.targetId));

                if (res.success) {
                    toast.success("Lot reverted successfully!", {
                        position: "top-right",
                        autoClose: 3000,
                    });
                    // Optional: refresh data or redirect
                    await getLotsForUser();
                } else {
                    toast.error(res.message, {
                        position: "top-right",
                    });
                }
            } catch (err) {
                console.log(err)
                toast.error(err.message, {
                    position: "top-center",
                });
            } finally {
                setLoading(false);
            }
        };
    };

    const fileColumns = React.useMemo(() => {
        const columns = [
            {
                header: "Sr. No.",
                cell: ({ row }) => pageIndex * pageSize + row.index + 1,
                meta: { width: "10%" },
                cellClassName: "text-center"
            },
            {
                header: "Lot Name",
                cell: ({ row }) => (
                    <span className="text-gray-800 font-medium block whitespace-pre-wrap underline hover:cursor-pointer text-orange-600" onClick={() => goToLot(row)}>
                        {row.original.lotName}
                    </span>
                ),
            },
            {
                header: "Total Sentences",
                cell: ({ row }) => (
                    <span className="text-gray-800 font-medium block whitespace-pre-wrap">
                        {row.original.totalSentences}
                    </span>
                ),
                // meta: { width: "35%" },
            },
            {
                header: "Target Language",
                accessorKey: "targetLangName",
                cell: ({ row }) => (
                    <span className="text-gray-800 font-medium block whitespace-pre-wrap">
                        {row.original.targetLangName}
                    </span>
                ),
                // meta: { width: "35%" },
            },
            {
                header: "Student Status",
                accessorKey: "studentStatus",
                cell: ({ row }) => (
                    <span
                        className={`font-semibold ${row.original.studentStatus === "PENDING"
                            ? "text-yellow-600"
                            : row.original.studentStatus === "VALIDATED"
                                ? "text-green-600"
                                : "text-gray-800"
                            }`}
                    >
                        {row.original.studentStatus || "PENDING"}
                    </span>
                ),
                // meta: { width: "10%" },
            },
            {
                header: "Reviewer Status",
                accessorKey: "reviewerStatus",
                cell: ({ row }) => (
                    <span
                        className={`font-semibold ${row.original.reviewerStatus === "PENDING"
                            ? "text-yellow-600"
                            : row.original.reviewerStatus === "VALIDATED"
                                ? "text-green-600"
                                : "text-gray-800"
                            }`}
                    >
                        {row.original.reviewerStatus || "PENDING"}
                    </span>
                ),
                // meta: { width: "10%" },
            },
            {
                header: "Completion Status",
                cell: ({ row }) => {
                    const validated = row.original.validatedSentencesCount || 0;
                    const reviewed = row.original.reviewedSentencesCount || 0;
                    const rejected = row.original.rejectedSentencesCount || 0;
                    const percent = row.original.completedPercentage || 0;

                    return (
                        <div className="w-full space-y-2">
                            <div className="text-md text-gray-600 flex justify-between ">
                                <span><strong>Validated:</strong> {validated}</span> | <span><strong>Reviewed:</strong> {reviewed}</span> | <span><strong>Rejected:</strong> {rejected}</span>
                            </div>

                            {/* Progress Bar */}
                            <div className="w-full bg-gray-200 rounded-full h-3">
                                <div
                                    className="bg-green-500 h-3 rounded-full transition-all duration-500"
                                    style={{ width: `${percent}%` }}
                                />
                            </div>

                            <div className="text-md font-semibold text-green-700 text-right">
                                {percent}% Completed
                            </div>
                        </div>
                    );
                },
            },
            {
                header: "Assigned At",
                accessorKey: "assignedAt",
                cellClassName: "cursor-pointer hover:text-blue-600 text-center",
                cell: ({ row }) => {
                    const deadline = row.original?.assignedAt;
                    if (!deadline) return "N/A";
                    try {
                        const date = new Date(deadline);
                        const now = new Date();
                        const isOverdue = date < now;
                        const isApproaching = !isOverdue && date - now < 7 * 24 * 60 * 60 * 1000;
                        return (
                            <div>
                                <div
                                    className={
                                        isOverdue
                                            ? "text-red-600"
                                            : isApproaching
                                                ? "text-amber-600"
                                                : null
                                    }
                                >
                                    {date.toLocaleDateString("en-US", {
                                        year: "numeric",
                                        month: "long",
                                        day: "numeric",
                                    })}
                                </div>
                                <div className="text-sm text-gray-500">
                                    {date.toLocaleTimeString("en-US", {
                                        hour: "numeric",
                                        minute: "numeric",
                                        hour12: true,
                                    })}
                                </div>
                            </div>
                        );
                    } catch (e) {
                        return deadline;
                    }
                },
            },
            {
                header: "Deadline",
                accessorKey: "deadline",
                cell: ({ row }) => {
                    const deadline = row.original?.deadline;
                    if (!deadline) return "N/A";
                    console.log("subTab:", subTab);
                    try {
                        const date = new Date(deadline);
                        const now = new Date();
                        const isOverdue = date < now;
                        const isApproaching = !isOverdue && date - now < 7 * 24 * 60 * 60 * 1000;
                        return (
                            <div>
                                <div
                                    className={
                                        isOverdue
                                            ? "text-red-600"
                                            : isApproaching
                                                ? "text-amber-600"
                                                : null
                                    }
                                >
                                    {date.toLocaleDateString("en-US", {
                                        year: "numeric",
                                        month: "long",
                                        day: "numeric",
                                    })}
                                </div>
                                <div className="text-sm text-gray-500">
                                    {date.toLocaleTimeString("en-US", {
                                        hour: "numeric",
                                        minute: "numeric",
                                        hour12: true,
                                    })}
                                </div>
                            </div>
                        );
                    } catch (e) {
                        return deadline;
                    }
                },
                // meta: { width: "35%" },
            },
            subTab === "assigned" && {
                header: "Buttons",
                cell: ({ row }) => {
                    const totalSentences = row.original.validatedSentencesCount + row.original.reviewedSentencesCount + row.original.rejectedSentencesCount;
                    console.log("Total Sentences:", totalSentences, "Row Total Sentences:", row.original.totalSentences);
                    return (
                        <div className='flex flex-col gap-2'>
                            {(row.original.studentStatus === 'ASSIGNED' || row.original.studentStatus === 'IN_PROGRESS' || role === 'REVIEWER') && <button
                                className="px-4 py-2 text-sm font-semibold text-white bg-blue-600 
                            hover:bg-blue-700 rounded-md shadow-sm min-w-[90px]
                            disabled:bg-gray-400 disabled:cursor-not-allowed disabled:opacity-70 disabled:hover:bg-gray-400"
                                disabled={Number(totalSentences) !== Number(row.original.totalSentences)}
                                onClick={() => submitLot(row)}
                            >
                                Submit
                            </button>}
                            {role === 'REVIEWER' && (
                                <button
                                    className="px-4 py-2 text-sm font-semibold text-white bg-orange-600 
        hover:bg-orange-700 rounded-md shadow-sm min-w-[90px]"
                                    onClick={() => revertLot(row)}
                                >
                                    Revert
                                </button>
                            )}
                        </div>
                    );
                },
            },
        ]

        return columns.filter(Boolean);

    }, [pageIndex, pageSize, targetName, handleTargetChange,subTab,role]);

    const showReviewComments = async (row) => {
        const sentenceId = row.original.sentenceId;

        setCommentsModal({
            isOpen: true,
            sentenceId,
            comments: [],
            loading: true
        });

        try {
            const url = role === 'STUDENT' ? `api/student/comments?sentenceId=${sentenceId}` : `api/reviewer/comments?sentenceId=${sentenceId}`;
            // Replace this with your real API endpoint
            const response = await apiClient(BASE_URL + url);

            setCommentsModal(prev => ({
                ...prev,
                comments: response.data || [],
                loading: false
            }));
        } catch (err) {
            console.log(err)
            toast.error("Failed to load comments.");
            setCommentsModal(prev => ({ ...prev, loading: false }));
        }
    };

    const markAsResolved = async (row) => {
        setPendingAction("markAsResolved");

        // 🟠 Ask user for confirmation
        toast.info(
            ({ closeToast }) => (
                <div className="flex flex-col items-center text-center px-4 py-2">
                    <p className="mb-4 text-gray-800 text-md sm:text-base font-medium">
                        Do you want to mark this sentence as resolved?
                    </p>

                    <div className="flex gap-3 justify-center">
                        {/* YES BUTTON */}
                        <button
                            onClick={async () => {
                                closeToast();
                                await handleMarkAsResolved();
                            }}
                            className="px-4 py-1.5 bg-[#ec7026] hover:bg-[#d15c14] text-white rounded-md font-medium text-sm shadow-sm transition-all duration-200"
                        >
                            ✅ Yes, mark as resolved
                        </button>

                        {/* CANCEL BUTTON */}
                        <button
                            onClick={() => {
                                toast.info("❌ Validation cancelled.", {
                                    position: "top-center",
                                    autoClose: 2000,
                                });
                                closeToast();
                                setPendingAction(null);
                            }}
                            className="px-4 py-1.5 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-md font-medium text-sm shadow-sm transition-all duration-200"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            ),
            {
                autoClose: false,
                closeOnClick: false,
                draggable: false,
                position: "top-center",
                hideProgressBar: true,
            }
        );

        // 🔹 Helper: Actual API call
        const handleMarkAsResolved = async () => {
            setLoading(true);
            setError(null);

            try {
                const absoluteIndex = pageIndex * pageSize + row.index;

                const payload = {
                    sentenceId: row.original.sentenceId,
                    reviewerId: userId,
                    lotId: row.original.lotId,
                    sourceText: row.original.sourceText
                };

                const res = await dispatch(resolveSentenceForReviwer(payload));

                if (res.success) {
                    toast.success("🎉 Sentence marked as resolved successfully!", {
                        position: "top-center",
                        autoClose: 2000,
                    });
                    setDataVersion(v => v + 1); // ← Force remount
                } else {
                    toast.error(res.message);
                }
            } catch (err) {
                console.error("❌ Error resolving sentence:", err);
                toast.error("⚠️ Failed to mark as resolved. Try again.", {
                    position: "top-center",
                });
            } finally {
                setPendingAction(null);
                setLoading(false);
            }
        };
    };


    const discardSentence = async (row) => {
        setLoading(true);
        setError(null);
        try {
            const absoluteIndex = pageIndex * pageSize + row.index;

            const res = await dispatch(discardSentenceForReviewer(row.original.sentenceId));

            if (res.success) {
                toast.success("🎉 Sentence discarded successfully!", {
                    position: "top-center",
                    autoClose: 2000,
                });
                setDataVersion(v => v + 1); // ← Force remount
                setDoubleClickedRow(null);
            } else {
                toast.error(`⚠️ Failed: ` + res.message, {
                    position: "top-center",
                });
            }
        } catch (err) {
            console.error("❌ Error discard sentence:", err);
            toast.error("⚠️ Failed to discard. Try again.", {
                position: "top-center",
            });
            setDoubleClickedRow(null);
        } finally {
            setLoading(false);
        }

    };



    const rejectedfileColumns = React.useMemo(() => [
        {
            header: "Sr. No.",
            cell: ({ row }) => pageIndex * pageSize + row.index + 1,
            meta: { width: "10%" },
            cellClassName: "text-center"
        },
        {
            header: "Source Text",
            cell: ({ row }) => {
                const index = row.index;
                const isEditing = editingRowIndex === index;

                return (
                    <div>
                        {isEditing ? (
                            <textarea
                                value={editedTexts[index] ?? row.original.sourceText}
                                onChange={(e) =>
                                    setEditedTexts({ ...editedTexts, [index]: e.target.value })
                                }
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring focus:ring-orange-300"
                                rows={3}
                            />
                        ) : (
                            <span>{row.original.sourceText}</span>
                        )}
                    </div>
                );
            },
            meta: { width: "75%" },
        },
        {
            header: "Actions",
            cell: ({ row }) => {
                const absoluteIndex = pageIndex * pageSize + row.index;
                const isEditing = editingRowIndex === absoluteIndex;

                return (
                    <div className="flex flex-col gap-2 justify-center items-center">
                        {/* Edit / Save Button */}
                        <button
                            onClick={() => {
                                if (isEditing) {
                                    // SAVE: Update the main data
                                    const newText = editedTexts[absoluteIndex] || row.original.sourceText;

                                    setFiles(prev => {
                                        const updated = [...prev];
                                        updated[absoluteIndex] = {
                                            ...updated[absoluteIndex],
                                            sourceText: newText.trim() || row.original.sourceText
                                        };
                                        return updated;
                                    });

                                    // Clean up
                                    setEditingRowIndex(null);
                                    setEditedTexts(prev => {
                                        const cleaned = { ...prev };
                                        delete cleaned[absoluteIndex];
                                        return cleaned;
                                    });

                                    toast.success("Source text updated!");
                                } else {
                                    // ENTER EDIT MODE
                                    if (editingRowIndex !== null) {
                                        toast.info("Please finish editing the current row first.");
                                        return;
                                    }
                                    setEditingRowIndex(absoluteIndex);
                                    // Initialize with current text
                                    setEditedTexts(prev => ({
                                        ...prev,
                                        [absoluteIndex]: row.original.sourceText
                                    }));
                                }
                            }}
                            className={`px-5 py-2 text-sm font-bold rounded-md shadow-md transition-all min-w-[100px]
              ${isEditing
                                    ? "bg-green-600 hover:bg-green-700 text-white"
                                    : "bg-[#ec7026] hover:bg-orange-700 text-white"
                                }`}
                        >
                            {isEditing ? "Save" : "Edit"}
                        </button>

                        {/* Regular Action Buttons */}
                        <button
                            className="px-4 py-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-md shadow-sm w-[90px]"
                            onClick={() => markAsResolved(row)}
                        >
                            Resolve
                        </button>

                        <button
                            className="px-4 py-2 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 rounded-md shadow-sm w-[90px]"
                            onClick={() => discardSentence(row)}
                        >
                            Discard
                        </button>
                    </div>
                );
            },
        },
    ], [
        pageIndex, pageSize, editingRowIndex, editedTexts, showReviewComments, tableData,
        markAsResolved, discardSentence, handleTargetChange
    ]);

    return (
        <div className=" min-h-screen">
            <Header />

            <div className="flex p-2">

                {/* Main Content */}
                <main
                    className={`flex-1 pl-4 pr-4 transition-all duration-300 `}
                >
                    <div className="bg-white shadow-lg rounded-2xl p-4 m-2 w-full">
                        {/* Top Bar */}
                        <div className="flex items-center justify-between mb-5">
                            <div className="flex items-center gap-3">
                                <button
                                    className="text-gray-700 hover:text-gray-900 focus:outline-none"
                                >
                                </button>
                                <h2 className="text-xl font-semibold text-gray-800">
                                    Lots for Text-to-Text Translation
                                </h2>
                            </div>

                            {role !== 'REVIEWER' &&
                                <div className="flex gap-3 items-center">
                                    <button
                                        onClick={() => pullLots(selectedLanguage)}
                                        className={`px-4 py-2 bg-green-600 text-white rounded-lg font-medium text-sm shadow-sm 
                    hover:bg-green-700 focus:ring-2 focus:ring-green-400 transition-all duration-200`}
                                    >
                                        Pull Lot
                                    </button>

                                    <button
                                        onClick={() => releaseLot()}
                                        disabled={!selectedRow}
                                        className={`px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium shadow-sm transition-all duration-200 ${selectedRow ? "" : "opacity-60 cursor-not-allowed"}`}
                                    >
                                        Release Lot
                                    </button>


                                    <select
                                        className="px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-700 font-medium text-sm
    hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-400 cursor-pointer transition-all duration-200"
                                        defaultValue=""
                                        value={selectedLanguage.languageId}
                                        onChange={handleLanguageChange}
                                    >
                                        <option value="" disabled>
                                            Select a language
                                        </option>
                                        {knownLanguages && knownLanguages.length > 0 &&
                                            knownLanguages.map((lang) => (
                                                <option
                                                    key={lang.languageId}
                                                    value={lang.languageId}
                                                    data-name={lang.languageName} // store language name in custom attribute
                                                >
                                                    {lang.languageName}
                                                </option>
                                            ))
                                        }
                                    </select>

                                </div>
                            }
                        </div>

                        {role === "REVIEWER" && (
                            <div className="flex gap-4 mb-6">
                                <button
                                    onClick={() => setActiveTab("active")}
                                    className={`px-4 py-2 rounded-lg font-medium transition ${activeTab === "active"
                                        ? "bg-amber-600 text-white"
                                        : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                                        }`}
                                >
                                    Lots
                                </button>

                                <button
                                    onClick={() => setActiveTab("rejected")}
                                    className={`px-4 py-2 rounded-lg font-medium transition ${activeTab === "rejected"
                                        ? "bg-red-600 text-white"
                                        : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                                        }`}
                                >
                                    Sentences
                                </button>
                            </div>
                        )}

                        {activeTab === "active" && (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">

                                {/* Assigned Card */}
                                <div
                                    className={`cursor-pointer p-4 rounded-xl shadow-md border transition-all ${subTab === "assigned"
                                        ? "border-amber-500 bg-amber-50"
                                        : "border-gray-200 hover:bg-gray-50"
                                        }`}
                                    onClick={() => setSubTab("assigned")}
                                >
                                    <div className="flex items-center gap-3">
                                        <span className="text-xl">📂</span>
                                        <div>
                                            <h3 className="text-md font-semibold text-gray-800">
                                                Assigned Lots
                                            </h3>
                                            <p className="text-sm text-gray-500">Currently under review</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Completed Card */}
                                <div
                                    className={`cursor-pointer p-4 rounded-xl shadow-md border transition-all ${subTab === "completed"
                                        ? "border-amber-500 bg-amber-50"
                                        : "border-gray-200 hover:bg-gray-50"
                                        }`}
                                    onClick={() => setSubTab("completed")}
                                >
                                    <div className="flex items-center gap-3">
                                        <span className="text-xl">✔️</span>
                                        <div>
                                            <h3 className="text-md font-semibold text-gray-800">
                                                Completed Lots
                                            </h3>
                                            <p className="text-sm text-gray-500">Reviewed & finalized</p>
                                        </div>
                                    </div>
                                </div>

                            </div>
                        )}



                        {/* Data Table */}
                        {files && files.length > 0 ? (
                            <div className="border border-gray-200 rounded-xl">
                                <GenericTableStudent
                                    data={activeTab === activeTab ? paginatedData : tableData}
                                    columns={activeTab === 'active' ? fileColumns : rejectedfileColumns}
                                    showPagination={true}
                                    manualPagination={true}
                                    pageIndex={pageIndex}
                                    pageSize={pageSize}
                                    hasNextPage={hasNextPage}
                                    onPaginationChange={({ pageIndex, pageSize }) => {
                                        setPageIndex(pageIndex);
                                        setPageSize(pageSize);
                                    }}
                                    options={{
                                        selectedRow,
                                        onRowSelect: (row) => setSelectedRow(row),
                                    }}

                                />
                            </div>
                        ) : (
                            <div className="text-center text-gray-500 py-10 text-lg">
                                No {activeTab === 'active' ? 'Lots' : 'sentences'} available for this user.
                            </div>
                        )}
                    </div>
                </main>
            </div>
            <CommentsModal
                isOpen={commentsModal.isOpen}
                comments={commentsModal.comments}
                loading={commentsModal.loading}
                onClose={() => setCommentsModal({ isOpen: false, sentenceId: null, comments: [], loading: false })}
            />
        </div>
    );
}

export default LotsForTTT
